import { useInfiniteQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import {
  ACCOUNT_ACTIVITY_DEMO_PAGES,
  type ActivityEvent,
} from 'src/page-components/Accounts/AccountActivityTab/accountActivityDemoData'
import { resolveCardManagementSource } from 'src/page-components/Accounts/cardManagementSource'
import { physicalCardManagementQueries } from 'src/queries/physicalCardManagement/physicalCardManagement.queries'

/**
 * Single data seam for the card-detail Activity tab (EX-40600).
 *
 * Reads `GET /creditcards/{creditCardId}/cardmanagement/activity` through
 * `physicalCardManagementQueries.activity`, an `infiniteQueryOptions` factory —
 * the same shape `transactionActivities` uses for the app's other
 * `nextCursor` feed. React Query owns page accumulation, the in-flight lock,
 * stale-response rejection, and unmount safety, so none of that is
 * reimplemented here.
 *
 * The response needs no mapping: the contract's page and the types in
 * `accountActivityDemoData` match field-for-field, including `occurredAt` as
 * epoch milliseconds and the explicitly-nullable `avatarUrl`, `detail`, and
 * `nextCursor`. The one narrowing is `actor.type` — the server enum has only
 * `USER` today while ours also admits `SYSTEM`, which is safe in that
 * direction.
 *
 * ## Real data, with the demo feed as a fallback
 *
 * `isDemoData` reports which of the two the caller is holding, and the tab
 * badges it. The rule is `resolveCardManagementSource`, shared with the
 * Statements tab — read it before widening anything, because the permissive
 * direction presents fabricated rows as a genuine audit history.
 *
 * The route is answered today by a NON-PRODUCTION mock controller
 * (`@Profile("!prod")`) whose responses carry
 * `X-Extend-Card-Management-Mock: true`. A 200 is not yet evidence that an
 * authoritative ledger exists. Keep the exposure in mind when changing the
 * gates: this tab is opened by the server capability
 * `READ_PHYSICAL_CARD_ACTIVITY` as well as by a client flag (see
 * `isUpdatedCardActivityEnabled`), so enrolling an organization on the backend
 * is by itself enough to reach it.
 */

/** The contract's default and maximum. `limit` outside 1..100 is a 400. */
const DEFAULT_PAGE_LIMIT = 50

/** The demo pages, in cursor-walk order. `Map` preserves insertion order. */
const DEMO_PAGES = [...ACCOUNT_ACTIVITY_DEMO_PAGES.values()]

export interface AccountActivityData {
  /** The accumulated feed across every page loaded so far, newest first. */
  events: ActivityEvent[]
  /** True only for the FIRST page — "load more" reports through `isLoadingMore`. */
  isLoading: boolean
  /** True while a subsequent page is in flight. */
  isLoadingMore: boolean
  /**
   * A genuine failure. Never set when the demo feed is showing — an
   * unprovisioned card is an answer, not an error. See
   * `resolveCardManagementSource`.
   */
  hasError: boolean
  /** False once the server returns a null `nextCursor` (terminal page). */
  hasMore: boolean
  /** No-op unless `hasMore` and nothing is already in flight. */
  loadMore: () => void
  /** Re-requests the feed from the start of the cursor walk. */
  retry: () => void
  /**
   * True when the feed below is FABRICATED because the endpoint had nothing for
   * this card. The tab keys its "Sample data" badge on this, so a real ledger
   * read renders without one.
   */
  isDemoData: boolean
}

/**
 * @param creditCardId - The card whose activity to read. `undefined` while the
 *   card is still loading; no request is made until it resolves.
 */
export const useAccountActivityData = (
  creditCardId: string | undefined,
): AccountActivityData => {
  const query = useInfiniteQuery(
    physicalCardManagementQueries.activity({
      creditCardId,
      limit: DEFAULT_PAGE_LIMIT,
    }),
  )

  const liveEvents = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data],
  )

  // The demo walk is NOT server state — the pages are a module constant — so it
  // is plain component state rather than a second query. Stored with the card
  // it belongs to and compared during render, which resets it on a card change
  // without an effect (the alternative would be render-driven work for
  // something that never touches the network).
  const [demoWalk, setDemoWalk] = useState({ creditCardId, pageCount: 1 })
  const demoPageCount =
    demoWalk.creditCardId === creditCardId ? demoWalk.pageCount : 1

  const demoEvents = useMemo(
    () => DEMO_PAGES.slice(0, demoPageCount).flatMap((page) => page.items),
    [demoPageCount],
  )

  // Destructured because React Query returns a NEW result object every render.
  // Closing over `query` would give `loadMore` and `retry` a fresh identity on
  // every render and make their `useCallback`s inert — the exact hazard the
  // hand-rolled version this replaced carried a `fetchPageRef` to avoid. These
  // five are stable: `fetchNextPage` and `refetch` are bound to the observer,
  // and the rest are compared by value.
  const { fetchNextPage, refetch, hasNextPage, isFetchingNextPage } = query

  // The shared rule, so this tab and Statements cannot drift apart on it.
  const source = resolveCardManagementSource({
    // `isLoading` covers the first fetch only; an unresolved card never starts
    // one, and must read as loading rather than as a settled empty feed.
    isLoading: query.isLoading || creditCardId === undefined,
    isError: query.isError,
    error: query.error,
    // A cursor counts as data even with no items on this page. `nextCursor` is
    // the contract's SOLE terminal signal, and a non-final page may legitimately
    // be empty — the statement-item walker already relies on that. Without the
    // second clause an empty-but-cursored first page resolves to `demo`, and
    // because `hasMore`/`loadMore` then take the demo branch, the real page
    // behind that cursor becomes unreachable for the rest of the session:
    // fabricated audit history replacing a real feed, with no way back.
    hasData: liveEvents.length > 0 || Boolean(hasNextPage),
  })

  const isDemoData = source === 'demo'

  const loadMore = useCallback(() => {
    if (isDemoData) {
      setDemoWalk({
        creditCardId,
        pageCount: Math.min(demoPageCount + 1, DEMO_PAGES.length),
      })
      return
    }
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [
    isDemoData,
    creditCardId,
    demoPageCount,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ])

  const retry = useCallback(() => {
    setDemoWalk({ creditCardId, pageCount: 1 })
    void refetch()
  }, [creditCardId, refetch])

  return {
    events: isDemoData ? demoEvents : liveEvents,
    isLoading: source === 'loading',
    // The demo walk is synchronous, so it is never "loading more".
    isLoadingMore: !isDemoData && isFetchingNextPage,
    hasError: source === 'error',
    // The CURSOR alone, never the verdict. React Query sets `isError` for a
    // failed page 2 exactly as for a failed page 1, so gating on `source`
    // unmounted the "Load more" control the moment a continuation failed —
    // leaving the feed's "couldn't load more" message with no control to act
    // on, while React Query was still holding the cursor. The verdict is not
    // load-bearing here anyway: a failed FIRST page has no pages and therefore
    // no `hasNextPage`.
    hasMore: isDemoData
      ? demoPageCount < DEMO_PAGES.length
      : Boolean(hasNextPage),
    loadMore,
    retry,
    isDemoData,
  }
}
